"use client";

import { useActorRef } from "@xstate/react";
import React, {
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
  useSyncExternalStore,
} from "react";
import {
  useParams,
  usePathname,
  useRouter,
  useSearchParams,
} from "next/navigation";
import "./shell.css";

// @storybook-internal wiring chassis: binds routes, the shared shell machine,
// thread history, and chat-v8 together. Public visual surfaces are catalogued
// independently with deterministic stories.

import {
  z_ChatV8ThreadList,
  type T_ChatV8ThreadListItem,
} from "@lattice/core-lib/types/chat-v8";
import {
  isShellNewChatActive,
  resolveShellRoute,
} from "@lattice/shared-frontend/shell";

import { ChatV8Client } from "@lattice/frontend/features/chat-v8/ChatV8Client";
import {
  MixpanelEvents,
  trackEvent,
} from "@lattice/frontend/lib/analytics/track";
import { useWorkspaceUser } from "@lattice/frontend/lib/contexts/workspace-user-context";
import { trpcApi } from "@lattice/frontend/lib/trpc/client";

import { PortfolioHomeCanvas } from "@lattice/frontend/features/portfolio-home/PortfolioHomeCanvas";

import { DesktopShellFrame } from "./DesktopShellFrame";
import { HomeSuggestionChips } from "./HomeSuggestionChips";
import {
  SettingsOverlay,
  type T_SettingsExternalDocument,
} from "./SettingsOverlay";
import { ShellFeatureSurface } from "./ShellFeatureSurface";
import { ShellHomeWelcome } from "./ShellHomeWelcome";
import { persistBrowserShellTimeZone } from "./shell-greeting-time";
import { persistShellHistoryPreference } from "./shell-history-preference";
import { isMobileShellHistoryMode } from "./shell-responsive";
import {
  hasWebSettingsIntent,
  parseWebSettingsIntent,
  removeWebSettingsIntentFromHref,
} from "./web-settings-intent";
import {
  getWebSettingsOpenEvent,
  webSettingsMachine,
} from "./web-settings-machine";

const SHELL_BASE = "/app/shell";
const THREAD_LIST_LIMIT = 50;
const NARROW_SHELL_MEDIA_QUERY = "(max-width: 767px)";

function subscribeToNarrowShell(listener: () => void): () => void {
  const mediaQuery = window.matchMedia(NARROW_SHELL_MEDIA_QUERY);
  mediaQuery.addEventListener("change", listener);
  return () => mediaQuery.removeEventListener("change", listener);
}

function readNarrowShellViewport(): boolean {
  return window.matchMedia(NARROW_SHELL_MEDIA_QUERY).matches;
}

/**
 * Persistent desktop frame for /app/shell routes. Web and mobile share shell
 * state, deterministic logic, and kit primitives — not page composition. The
 * frame remains mounted while route state selects home, new chat, or a thread.
 */
export function ChatFirstShell({
  appEnvironment,
  children,
  initialDesktopHistoryOpen,
  initialGreetingHour,
  initialIsMobile,
  logtoEndpoint,
  paymentEnabled,
  revenueCatApiKey,
}: {
  appEnvironment: string;
  children: ReactNode;
  initialDesktopHistoryOpen: boolean;
  initialGreetingHour: number | null;
  initialIsMobile: boolean;
  logtoEndpoint: string;
  paymentEnabled: boolean;
  revenueCatApiKey: string;
}) {
  const router = useRouter();
  const { firstName } = useWorkspaceUser();
  const pathname = usePathname() ?? SHELL_BASE;
  const searchParams = useSearchParams();
  const settingsIntentQuery = searchParams.toString();
  const messageIntent = searchParams.get("message");
  const prefillIntent = searchParams.get("prefill");
  const prefilledMessage = prefillIntent ?? messageIntent ?? undefined;
  const params = useParams<{
    stock_symbol?: string;
    threadCuid?: string;
  }>();
  const route = resolveShellRoute(
    pathname.replace(/^\/app/, "") || "/shell",
    params.threadCuid,
  );
  const settingsActor = useActorRef(webSettingsMachine);
  const readInitialNarrowViewport = useCallback(
    () => initialIsMobile,
    [initialIsMobile],
  );
  const isNarrowViewport = useSyncExternalStore(
    subscribeToNarrowShell,
    readNarrowShellViewport,
    readInitialNarrowViewport,
  );
  const mobileHistoryMode = isMobileShellHistoryMode({
    initialIsMobile,
    isNarrowViewport,
  });
  const [desktopHistoryOpen, setDesktopHistoryOpen] = useState(
    initialDesktopHistoryOpen,
  );
  const [mobileHistoryOpen, setMobileHistoryOpen] = useState(false);
  const railOpen = mobileHistoryMode ? mobileHistoryOpen : desktopHistoryOpen;
  const trpcUtils = trpcApi.useUtils();
  const threadsQuery = trpcApi.chat_v8.listThreads.useQuery({
    limit: THREAD_LIST_LIMIT,
  });
  const renameThreadMutation = trpcApi.chat_v8.renameThread.useMutation();
  const deleteThreadMutation = trpcApi.chat_v8.deleteThread.useMutation();
  const { refetch: refetchThreads } = threadsQuery;
  const parsedThreads = z_ChatV8ThreadList.safeParse(threadsQuery.data?.data);
  const threads: T_ChatV8ThreadListItem[] = parsedThreads.success
    ? parsedThreads.data.threads
    : [];

  const isStockRoute = pathname.startsWith(`${SHELL_BASE}/stocks/`);
  // Memoized so callbacks depending on it keep stable identities under the
  // react-hooks memoization rule.
  const activeThreadCuid = useMemo(
    () => (!isStockRoute && route.kind === "thread" ? route.threadCuid : null),
    [isStockRoute, route],
  );
  const isHome =
    !isStockRoute && (route.kind === "home" || route.kind === "invalid");
  const stockTitle = params.stock_symbol?.trim().toUpperCase() ?? null;

  // The greeting remains server-stable for this render. Persist the browser's
  // IANA zone for the next server navigation/refresh instead of swapping copy
  // after hydration.
  // eslint-disable-next-line no-restricted-syntax -- synchronize the browser time zone to an SSR-readable cookie without changing greeting state in this render
  useEffect(() => {
    persistBrowserShellTimeZone();
  }, []);

  // URL state is an external entry/return boundary only. Consume it into the
  // settings machine, then remove only the intent-owned query parameters.
  // eslint-disable-next-line no-restricted-syntax -- synchronize validated external URL intent into the shell state machine
  useEffect(() => {
    const currentParams = new URLSearchParams(settingsIntentQuery);
    if (!hasWebSettingsIntent(currentParams)) return;

    const intent = parseWebSettingsIntent(currentParams);
    if (intent) {
      trackEvent(MixpanelEvents.SHELL_SETTINGS_OPENED, {
        via: "external_intent",
      });
      settingsActor.send(
        getWebSettingsOpenEvent(intent.panel, {
          accountUpdate: intent.accountUpdate,
          referralCode: intent.referralCode,
        }),
      );
    }

    router.replace(removeWebSettingsIntentFromHref(pathname, currentParams), {
      scroll: false,
    });
  }, [pathname, router, settingsActor, settingsIntentQuery]);

  const handleMessageStarted = useCallback(() => {
    if (!isHome) return;
    trackEvent(MixpanelEvents.SHELL_NEW_CHAT_STARTED);
    router.replace(`${SHELL_BASE}/new`);
  }, [isHome, router]);

  const handleToggleHistory = useCallback(() => {
    const next = !railOpen;
    if (mobileHistoryMode) {
      setMobileHistoryOpen(next);
    } else {
      setDesktopHistoryOpen(next);
      persistShellHistoryPreference(next);
    }
    if (next) {
      trackEvent(MixpanelEvents.SHELL_DRAWER_OPENED, { via: "hamburger" });
      void refetchThreads();
    }
  }, [mobileHistoryMode, railOpen, refetchThreads]);

  const handleNewChat = useCallback(() => {
    if (mobileHistoryMode) setMobileHistoryOpen(false);
    router.push(SHELL_BASE);
  }, [mobileHistoryMode, router]);

  const handleRenameThread = useCallback(
    async (threadCuid: string, name: string) => {
      await renameThreadMutation.mutateAsync({ name, threadCuid });
      void trpcUtils.chat_v8.listThreads.invalidate();
    },
    [renameThreadMutation, trpcUtils.chat_v8.listThreads],
  );

  const handleDeleteThread = useCallback(
    async (threadCuid: string) => {
      await deleteThreadMutation.mutateAsync({ threadCuid });
      void trpcUtils.chat_v8.listThreads.invalidate();
      if (threadCuid === activeThreadCuid) {
        if (mobileHistoryMode) setMobileHistoryOpen(false);
        router.replace(SHELL_BASE);
      }
    },
    [
      activeThreadCuid,
      deleteThreadMutation,
      mobileHistoryMode,
      router,
      trpcUtils.chat_v8.listThreads,
    ],
  );

  const handleOpenSettings = useCallback(() => {
    if (mobileHistoryMode) setMobileHistoryOpen(false);
    trackEvent(MixpanelEvents.SHELL_SETTINGS_OPENED);
    settingsActor.send({ type: "OPEN_SETTINGS" });
  }, [mobileHistoryMode, settingsActor]);

  const handleOpenManualHoldings = useCallback(() => {
    if (mobileHistoryMode) setMobileHistoryOpen(false);
    trackEvent(MixpanelEvents.SHELL_SETTINGS_OPENED);
    settingsActor.send({ type: "OPEN_MANUAL_HOLDINGS" });
  }, [mobileHistoryMode, settingsActor]);

  const handleOpenBrokerageConnect = useCallback(() => {
    if (mobileHistoryMode) setMobileHistoryOpen(false);
    trackEvent(MixpanelEvents.SHELL_SETTINGS_OPENED);
    settingsActor.send({ type: "OPEN_BROKERAGE_CONNECT" });
  }, [mobileHistoryMode, settingsActor]);

  const handleOpenSettingsDocument = useCallback(
    (destination: T_SettingsExternalDocument) => {
      settingsActor.send({ type: "CLOSE_SETTINGS" });
      router.push(destination);
    },
    [router, settingsActor],
  );

  return (
    <>
      <DesktopShellFrame
        activeThreadCuid={activeThreadCuid}
        historyOpen={railOpen}
        newChatActive={isShellNewChatActive(route)}
        onDeleteThread={handleDeleteThread}
        onNewChat={handleNewChat}
        onOpenSettings={handleOpenSettings}
        onRenameThread={handleRenameThread}
        onSelectThread={(threadCuid) => {
          if (mobileHistoryMode) setMobileHistoryOpen(false);
          trackEvent(MixpanelEvents.SHELL_THREAD_SELECTED, {
            thread_cuid: threadCuid,
          });
          router.push(`${SHELL_BASE}/${threadCuid}`);
        }}
        onToggleHistory={handleToggleHistory}
        threadTitle={
          isStockRoute
            ? stockTitle
            : activeThreadCuid
              ? (threads.find(
                  (thread) => thread.threadCuid === activeThreadCuid,
                )?.threadName ?? "Conversation")
              : null
        }
        threads={threads}
      >
        {isStockRoute ? (
          <ShellFeatureSurface>{children}</ShellFeatureSurface>
        ) : (
          <ChatV8Client
            autoSubmit={prefillIntent === null && messageIntent !== null}
            composerSuggestionSurface={
              isNarrowViewport ? (isHome ? "home" : "conversation") : undefined
            }
            emptyContent={
              isHome
                ? (sendPrompt) => (
                    <div className="shell-home">
                      <ShellHomeWelcome
                        firstName={firstName}
                        hour={initialGreetingHour}
                      />
                      <PortfolioHomeCanvas
                        onAddManualHoldings={handleOpenManualHoldings}
                        onConnectBrokerage={handleOpenBrokerageConnect}
                      />
                      {/* Page content, not dock chrome: the pills sit below
                          holdings and are pushed down when holdings expand
                          (RAI-1089). */}
                      <HomeSuggestionChips onPress={sendPrompt} />
                    </div>
                  )
                : undefined
            }
            onMessageStarted={handleMessageStarted}
            onThreadListChanged={() => void refetchThreads()}
            prefilledMessage={prefilledMessage}
            // Home pills live in the page above; the dock keeps only the
            // composer-focus suggestions.
            renderStarterPrompts={isHome ? () => undefined : undefined}
            threadCuid={activeThreadCuid}
          />
        )}
      </DesktopShellFrame>
      <SettingsOverlay
        actor={settingsActor}
        appEnvironment={appEnvironment}
        logtoEndpoint={logtoEndpoint}
        onOpenExternalDocument={handleOpenSettingsDocument}
        paymentEnabled={paymentEnabled}
        revenueCatApiKey={revenueCatApiKey}
      />
    </>
  );
}
